import { msAPI } from "./index.js";

// ==========================================================================
// SFTP feed configuration (menu-service `seller.import_settings`)
//
// Ported from stockroom's SellerFTPDialog. menu-service exposes merchant-scoped
// twins of the retailer routes — `/merchant/:x_store_id/ftp-config`, guarded by
// `merchantUserLoggedIn` — which are the ones this dashboard should use.
// ==========================================================================

/**
 * Provision SFTP credentials for a store that has none.
 *
 * Takes no body: the server generates the account against our own SFTP host.
 * **saucey_admin only** — the handler returns 401 for anyone else — and it
 * refuses with 400 "credentials already exist" if the store is already on our
 * host.
 *
 * @returns {Promise<object>} the updated seller
 */
export const createSftpCredentials = async (storeId) => {
  return (await msAPI.post(`merchant/${encodeURIComponent(storeId)}/ftp-config`)).data;
};

/**
 * Replace a store's import settings.
 *
 * **This is a full replace of `import_settings`, not a merge.** The handler
 * builds `$set: { import_settings: <body> }`, so any key you omit is deleted —
 * including the whole `credentials` object. Always send the complete settings
 * you read back from the server, never a partial form.
 *
 * @param {string} storeId
 * @param {object} importSettings complete `import_settings`
 * @returns {Promise<object>} the updated seller, un-minified (so it carries
 *   `import_settings` back, which the merchant store endpoint does not)
 */
export const updateSftpConfig = async (storeId, importSettings) => {
  return (await msAPI.patch(
    `merchant/${encodeURIComponent(storeId)}/ftp-config`,
    importSettings
  )).data;
};

/**
 * Read a store's current import settings.
 *
 * NOTE: this endpoint does not exist yet. `GET /merchant/seller` runs the seller
 * through `toShortDirectAdminDisplay`, which carries `api_credentials` but drops
 * `import_settings`, and there is no other merchant-scoped read for it. The
 * caller treats a 404/405 as "cannot read" and disables saving, because a
 * partial write here is destructive (see `updateSftpConfig`).
 */
export const getSftpConfig = async (storeId) => {
  return (await msAPI.get(`merchant/${encodeURIComponent(storeId)}/ftp-config`)).data;
};

/** S3 prefixes offered for `credentials.processed_output_path`. */
export const getInventoryOutputFolders = async () => {
  return (await msAPI.get("retailer/config/output-locations")).data;
};
